<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Icon from 'next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';

const { contentAttributes, id, conversationId } = useMessageContext();
const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();

const recordings = ref(
  (contentAttributes.value.jarabot?.recordingUrls || []).map(url => ({
    url,
    blobUrl: '',
    loading: false,
    failed: false,
    unavailable: false,
  }))
);

// Native <audio> seeking breaks in Chrome when the recording host does not
// support HTTP range requests (the Jarabot PBX proxy returns 200 for every
// request and ignores the `Range` header). To make the slider reliable, on the
// first play we download the whole file and play it from an in-memory blob:
// URL, which is fully seekable without range requests.
// Note: the fetch is cross-origin, so the PBX proxy must send an
// `Access-Control-Allow-Origin` header for the Chatwoot dashboard origin.
const loadAndPlay = async (event, recording) => {
  // Already playing from a downloaded blob, a download is in progress, or it
  // failed once already — let the native player handle play/seek as usual.
  if (recording.blobUrl || recording.loading || recording.failed) return;

  const audio = event.target;
  audio.pause();
  recording.loading = true;

  try {
    const response = await fetch(recording.url);
    // The PBX deletes old recordings. A 404 means that the file is gone for
    // good, so replace the player with a notice.
    if (response.status === 404) {
      recording.unavailable = true;
      return;
    }
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    recording.blobUrl = URL.createObjectURL(await response.blob());
    audio.src = recording.blobUrl;
  } catch {
    // Download failed (e.g. missing CORS header) — fall back to direct
    // streaming, which plays but is not reliably seekable.
    recording.failed = true;
  } finally {
    recording.loading = false;
  }

  if (recording.unavailable) return;
  audio.play().catch(() => {});
};

// MEDIA_ERR_ABORTED fires whenever we swap `audio.src` to the blob URL mid-load
// (and on `audio.pause()`). That's expected here, so ignore it; treat any other
// media error as a real failure so the user is not stuck on the spinner.
const handleAudioError = (event, recording) => {
  const error = event.target.error;
  if (!error || error.code === error.MEDIA_ERR_ABORTED) return;
  recording.failed = true;
  recording.loading = false;
};

// Only administrators may delete the recordings (the API checks it too). They are deleted on the PBX
// for good, all of them at once. The message then turns into a note saying who deleted them.
const deleteDialogRef = ref(null);
const isDeleting = ref(false);

const deleteRecording = async () => {
  isDeleting.value = true;
  try {
    await store.dispatch('deleteMessageRecording', {
      conversationId: conversationId.value,
      messageId: id.value,
    });
    useAlert(t('COMPONENTS.FILE_BUBBLE.DELETE_RECORDING.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('COMPONENTS.FILE_BUBBLE.DELETE_RECORDING.ERROR')
    );
  } finally {
    isDeleting.value = false;
    deleteDialogRef.value?.close();
  }
};

onBeforeUnmount(() => {
  recordings.value.forEach(({ blobUrl }) => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  });
});
</script>

<template>
  <BaseBubble class="bg-transparent" data-bubble-name="audio">
    <div
      v-for="recording in recordings"
      :key="recording.url"
      class="flex items-center gap-2"
    >
      <div
        v-if="recording.unavailable"
        class="flex items-center gap-1 px-3 py-2 rounded-lg bg-n-alpha-1"
      >
        <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
        <p class="mb-0 text-n-slate-11">
          {{ $t('COMPONENTS.FILE_BUBBLE.RECORDING_UNAVAILABLE') }}
        </p>
      </div>
      <audio
        v-else
        controls
        preload="none"
        :src="recording.url"
        @play="loadAndPlay($event, recording)"
        @error="handleAudioError($event, recording)"
      />
      <span
        v-if="recording.loading"
        class="w-4 h-4 border-2 rounded-full border-n-slate-6 border-t-n-slate-11 animate-spin"
      />
    </div>
    <template v-if="isAdmin">
      <Button
        :label="t('COMPONENTS.FILE_BUBBLE.DELETE_RECORDING.BUTTON')"
        icon="i-lucide-trash-2"
        variant="link"
        color="ruby"
        size="xs"
        class="mt-1"
        @click="deleteDialogRef?.open()"
      />
      <Dialog
        ref="deleteDialogRef"
        type="alert"
        :title="t('COMPONENTS.FILE_BUBBLE.DELETE_RECORDING.TITLE')"
        :description="t('COMPONENTS.FILE_BUBBLE.DELETE_RECORDING.DESCRIPTION')"
        :confirm-button-label="
          t('COMPONENTS.FILE_BUBBLE.DELETE_RECORDING.CONFIRM')
        "
        :is-loading="isDeleting"
        @confirm="deleteRecording"
      />
    </template>
  </BaseBubble>
</template>
